// src/components/leader/UploadManager.js
//
// App-wide register of running uploads (audit FL-10). A page that starts an upload
// can be left: the request keeps running, the navbar shows its progress, the
// beforeunload guard and the screen wake lock stay on until it finishes, and a
// toast reports the result when the page is no longer open.
import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { UploadCloud } from 'lucide-react';
import useUploadGuard from '../../hooks/useUploadGuard';

const UploadApiContext = createContext(null);
const UploadListContext = createContext([]);

export function UploadProvider({ children }) {
  const [uploads, setUploads] = useState({});

  const update = useCallback((id, patch) => {
    setUploads((map) => ({ ...map, [id]: { ...(map[id] || {}), ...patch, id } }));
  }, []);

  const remove = useCallback((id) => {
    setUploads((map) => {
      if (!map[id]) return map;
      const next = { ...map };
      delete next[id];
      return next;
    });
  }, []);

  const list = useMemo(() => Object.values(uploads), [uploads]);
  const api = useMemo(() => ({ update, remove }), [update, remove]);

  useUploadGuard(list.length > 0);

  return (
    <UploadApiContext.Provider value={api}>
      <UploadListContext.Provider value={list}>{children}</UploadListContext.Provider>
    </UploadApiContext.Provider>
  );
}

/** {update, remove} when an UploadProvider is present, otherwise null. */
export function useUploadManager() {
  return useContext(UploadApiContext);
}

/** Running uploads (empty without a provider). */
export function useActiveUploads() {
  return useContext(UploadListContext);
}

/** Compact progress pill for the navbar. Renders nothing when no upload runs. */
export function UploadIndicator() {
  const uploads = useActiveUploads();
  if (uploads.length === 0) return null;
  const progress = Math.round(uploads.reduce((sum, u) => sum + (u.progress || 0), 0) / uploads.length);
  const processing = uploads.every((u) => u.status === 'processing');
  const names = uploads.map((u) => u.fileName).filter(Boolean).join(', ');
  return (
    <div
      className="inline-flex items-center gap-1.5 rounded-full bg-indigo-50 px-2.5 py-1 text-xs font-medium text-indigo-700"
      role="status"
      title={names ? `Nahrávání: ${names}` : undefined}
    >
      <UploadCloud className="h-4 w-4 animate-pulse" aria-hidden="true" />
      <span className="hidden sm:inline">{processing ? 'Zpracování…' : 'Nahrávání'}</span>
      <span>{processing ? '' : `${progress} %`}</span>
      <span className="sr-only">. Nezavírejte stránku, dokud nahrávání neskončí.</span>
    </div>
  );
}
